import { computed, ref } from 'vue'
import { api } from '@/global/services/api'
import { isArray, isFunction } from '@/global/utils/utils'
import { useTableService } from '@/global/composables/table/useTableService'
import { showDetailsAction } from '@/global/components/table/helpers/tableActions'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import { buildTaskStreetsEndpoint } from '@/modules/road-maintenance-module/winter-service-v2/tasks/config/constants'
import {
  WORK_ORDERS_ENDPOINT,
  WORK_ORDERS_OVERVIEW_TRANSLATION_NAMESPACE as NS,
  WORK_ORDER_STREETS_TABLE_KEY
} from '@/modules/road-maintenance-module/winter-service-v2/work-orders/overview/config/constants'

/**
 * The "Pregled ulica" tab of a work order - the same route listing the task details
 * screen carries, with nothing to change: an order is what the plan froze, and the
 * streets behind it are edited on the task itself.
 *
 * The streets hang under the task rather than under the order, and the listing row
 * does not reach this screen, so the order is asked for its task first.
 */
export function useWorkOrderStreets (options) {
  const { translate, workOrderId, openStreetDialogFn } = options || {}

  const { buildConfig } = useTableService()

  // What the table has listed, kept so the map draws the same set without a request of
  // its own.
  const streets = ref([])
  const taskId = ref(null)

  const loadTaskId = async () => {
    try {
      const { data } = await api(WINTER_SERVICE_MODULE).get(`${WORK_ORDERS_ENDPOINT}/${workOrderId}`)
      taskId.value = data?.task_data?.task?.id || null
    }
    catch (exception) {
      console.error(exception)
      taskId.value = null
    }
  }

  const endpoint = computed(() => taskId.value ? buildTaskStreetsEndpoint(taskId.value) : null)

  // The map draws exactly what the table has listed, so it is fed off the same response
  // rather than fetching the route a second time.
  const onDataReceived = (apiData) => {
    streets.value = isArray(apiData?.data) ? apiData.data : []
  }

  /**
   * Opens a street. Reached two ways - from its row in the table, which hands over a
   * table event, and from its route on the map, which hands over the street itself -
   * so both shapes are accepted. It only ever shows what the street says.
   */
  const onOpenStreet = (event) => {
    const street = event?.data ?? event

    if (!street?.id || !isFunction(openStreetDialogFn)) {
      return
    }

    openStreetDialogFn({
      title: translate(`${NS}.street_details`),
      street
    })
  }

  // The listing reports the path type as its own object and leaves a polygon's length,
  // width and passes empty - the columns say so rather than showing a blank.
  const cellsConfig = computed(() => ({
    path_type: {
      custom_display_value: (data) => data?.path_type?.description || data?.path_type?.name || ''
    },
    passes: {
      custom_display_value: (data) => data?.passes ?? '-'
    },
    length: {
      custom_display_value: (data) => data?.length ?? '-'
    },
    width: {
      custom_display_value: (data) => data?.width ?? '-'
    },
    area: {
      custom_display_value: (data) => data?.area ?? '-'
    }
  }))

  const headers = computed(() => ([
    {
      label: translate(`${NS}.id`),
      key: 'id',
      always_visible: true
    },
    {
      label: translate(`${NS}.street_type`),
      key: 'path_type',
      sortable: true,
      always_visible: true
    },
    {
      label: translate(`${NS}.street_name`),
      key: 'street_name',
      sortable: true,
      always_visible: true
    },
    {
      label: translate(`${NS}.passes`),
      key: 'passes',
      sortable: true,
      always_visible: true
    },
    {
      label: translate(`${NS}.length_m`),
      key: 'length',
      sortable: true,
      always_visible: true
    },
    {
      label: translate(`${NS}.width_m`),
      key: 'width',
      sortable: true,
      always_visible: true
    },
    {
      label: translate(`${NS}.area_m2`),
      key: 'area',
      sortable: true,
      always_visible: true
    },
    {
      label: translate(`${NS}.city_district`),
      key: 'street_district',
      sortable: true,
      always_visible: true
    }
  ]))

  // A street can be looked at and nothing else - this screen never changes one.
  const actionsConfig = computed(() => ([
    showDetailsAction(translate, onOpenStreet)
  ]))

  const tableConfig = computed(() => buildConfig({
    key: WORK_ORDER_STREETS_TABLE_KEY,
    // The table lives inside a tab, not on a screen of its own - it fills the tab and
    // scrolls its rows internally.
    fillHeight: true,
    apiConfig: {
      get: {
        module: WINTER_SERVICE_MODULE,
        route: endpoint.value
      }
    },
    onDataReceived,
    // Nothing in the toolbar rows applies: the tab shows one order's whole route, so
    // there is nothing to search, filter or refresh by hand, and the columns are the
    // street's own handful.
    filters_and_columns_visibility_config: {
      showRefreshButton: false,
      showFiltersButton: false,
      showColumnsVisibilityButton: false,
      showTableGridSwitchButton: false
    },
    globalStyle: {
      // With no toolbar row above it the table starts at the container's own padding,
      // which left it sitting lower than the map next to it.
      containerStyle: {
        padding: 0
      }
    },
    tableConfig: {
      cellsConfig: cellsConfig.value,
      headers: headers.value,
      actionsConfig: actionsConfig.value
    }
  }))

  return {
    streets,
    taskId,
    loadTaskId,
    tableConfig,
    onOpenStreet
  }
}
